import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { KeyRound, ShieldCheck, Loader2 } from "lucide-react";
import { waLink, PHONE_DISPLAY } from "@/lib/api";
import { WhatsAppIcon } from "@/components/WhatsAppFloat";

const DURATION = 20;

const LlaveDigital = () => {
  const [params] = useSearchParams();
  const ref = params.get("ref") || "";
  const visita = params.get("visita") || "";
  const [state, setState] = useState("idle"); // idle | running | done
  const [pct, setPct] = useState(0);
  const [left, setLeft] = useState(DURATION);

  useEffect(() => {
    document.title = "Instalación de llave digital | Inmobiliaria Tecnohogar";
  }, []);

  useEffect(() => {
    if (state !== "running") return;
    const t0 = Date.now();
    const iv = setInterval(() => {
      const elapsed = (Date.now() - t0) / 1000;
      const p = Math.min(100, Math.round((elapsed / DURATION) * 100));
      setPct(p);
      setLeft(Math.max(0, Math.ceil(DURATION - elapsed)));
      if (p >= 100) {
        clearInterval(iv);
        setState("done");
      }
    }, 200);
    return () => clearInterval(iv);
  }, [state]);

  const waText = `Hola Inmobiliaria Tecnohogar, ya estoy en la puerta de la vivienda${ref ? ` (Ref. ${ref})` : ""}. ¿Me abrís el acceso, por favor?`;

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink px-5 py-10" data-testid="llave-digital-page">
      <div className="w-full max-w-md rounded-2xl border border-gold/25 bg-ink2/90 p-8 text-center shadow-[0_10px_30px_rgba(0,0,0,0.8)]">
        <p className="flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-wa" data-testid="llave-secure-label">
          <ShieldCheck size={14} /> Conexión segura
        </p>
        <div className="mx-auto mt-6 flex h-20 w-20 items-center justify-center rounded-full border border-gold/40 bg-gold/10 text-gold">
          <KeyRound size={34} />
        </div>
        <h1 className="mt-5 font-serif text-3xl text-ivory">Llave digital</h1>
        <p className="mt-2 text-sm text-stone2">Instala tu llave digital para la visita.</p>
        <p className="text-sm text-stone2">Configura la llave digital de la vivienda antes de tu visita.</p>

        {(ref || visita) && (
          <div className="mt-4 flex flex-wrap justify-center gap-2" data-testid="llave-visit-info">
            {ref && <span className="rounded-full border border-gold/30 px-3 py-1 text-xs text-gold">Ref. {ref}</span>}
            {visita && <span className="rounded-full border border-gold/30 px-3 py-1 text-xs text-gold">Visita: {visita}</span>}
          </div>
        )}

        {state === "idle" && (
          <button
            onClick={() => setState("running")}
            data-testid="llave-start-button"
            className="btn-gold-metallic mt-8 w-full rounded-full py-3.5 text-sm font-bold uppercase tracking-[0.2em] text-ink"
          >
            Iniciar
          </button>
        )}

        {state === "running" && (
          <div className="mt-8" data-testid="llave-progress-panel">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold-subtle">Asistente de llave digital · Cifrado</p>
            <p className="mt-3 text-sm text-ivory/85">Configurando la llave digital para la visita…</p>
            <p className="text-xs text-stone2">No cierres esta ventana.</p>
            <div className="mt-5 h-2.5 w-full overflow-hidden rounded-full bg-ink">
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-[#BF953F] via-[#FCF6BA] to-[#AA771C]"
                style={{ width: `${pct}%` }}
                data-testid="llave-progress-bar"
              />
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-stone2">
              <span>{pct}%</span>
              <span data-testid="llave-progress-timer">{left}s restantes</span>
            </div>
            <Loader2 size={16} className="mx-auto mt-4 animate-spin text-gold" />
          </div>
        )}

        {state === "done" && (
          <div className="mt-8" data-testid="llave-done-panel">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-wa">Cifrado completado</p>
            <p className="mt-3 font-serif text-2xl text-ivory">Llave digital lista</p>
            <p className="mt-2 text-sm text-stone2">
              Ya puedes abrir la vivienda con la app de la cerradura en tu franja horaria. Al salir, la puerta se cierra
              y la alarma se arma sola.
            </p>
            <a
              href={waLink(waText)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="llave-whatsapp-button"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-wa px-6 py-3 font-semibold text-ink transition-transform hover:-translate-y-0.5"
            >
              <WhatsAppIcon className="h-5 w-5" /> Ya estoy en la puerta
            </a>
            <p className="mt-4 text-xs text-stone2">Cualquier duda: {PHONE_DISPLAY}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LlaveDigital;
